import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  ArrowLeft, Paperclip, X, Calendar as CalendarIcon, Clock, Send, FileText,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { leaveService } from '@/services';
import { hoursBetween } from '@/utils/format';

const TYPE_OPTIONS = [
  { value: 'casual', label: 'Casual leave', hint: 'Personal time off' },
  { value: 'sick', label: 'Sick leave', hint: 'Illness or medical' },
  { value: 'paid', label: 'Paid leave', hint: 'Earned / planned' },
  { value: 'wfh', label: 'Work From Home', hint: 'Working remotely' },
  { value: 'half-day', label: 'Half Day', hint: 'Half-day off' },
  { value: 'short', label: 'Short Leave (hourly)', hint: 'Few hours off — pick the time window' },
];

export default function NewLeave() {
  const navigate = useNavigate();
  const today = dayjs().format('YYYY-MM-DD');
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      type: 'casual',
      startDate: today,
      endDate: today,
      startTime: '10:00',
      endTime: '12:00',
      reason: '',
    },
  });
  const [file, setFile] = useState(null);

  const type = watch('type');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const startTime = watch('startTime');
  const endTime = watch('endTime');

  const isShort = type === 'short';
  const shortHours = isShort ? hoursBetween(startTime, endTime) : 0;
  const dayCount = !isShort && startDate && endDate
    ? Math.max(1, dayjs(endDate).startOf('day').diff(dayjs(startDate).startOf('day'), 'day') + 1)
    : 0;
  const typeMeta = TYPE_OPTIONS.find((t) => t.value === type);

  const onSubmit = async (data) => {
    try {
      const fd = new FormData();
      fd.append('type', data.type);
      fd.append('startDate', data.startDate);
      fd.append('reason', data.reason);
      if (data.type === 'short') {
        fd.append('startTime', data.startTime);
        fd.append('endTime', data.endTime);
      } else {
        fd.append('endDate', data.endDate);
      }
      if (file) fd.append('attachment', file);
      await leaveService.apply(fd);
      toast.success('Leave request submitted');
      navigate('/leaves');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/leaves" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600">
        <ArrowLeft size={14} /> Back to leaves
      </Link>

      <PageHeader
        title="Apply for leave"
        subtitle="Choose the type and fill in the details — admin will review your request"
      />

      <form id="newLeaveForm" onSubmit={handleSubmit(onSubmit)} className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2">
              <CalendarIcon size={16} /> Leave type
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {TYPE_OPTIONS.map((opt) => {
                const checked = type === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                      checked
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                        : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <input type="radio" value={opt.value} className="mt-1" {...register('type', { required: true })} />
                    <div className="min-w-0">
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-xs text-slate-500">{opt.hint}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </Card>

          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2">
              {isShort ? <Clock size={16} /> : <CalendarIcon size={16} />}
              {isShort ? 'Date & time window' : 'Date range'}
            </div>
            {isShort ? (
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3">
                  <label className="label">Date</label>
                  <input type="date" className="input" {...register('startDate', { required: true })} />
                </div>
                <div>
                  <label className="label">From</label>
                  <input type="time" className="input" {...register('startTime', { required: true })} />
                </div>
                <div>
                  <label className="label">To</label>
                  <input type="time" className="input" {...register('endTime', { required: true })} />
                </div>
                <div className="flex items-end">
                  <div className={`w-full px-3 py-2 rounded-lg text-sm font-semibold text-center ${
                    shortHours <= 0
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                      : shortHours > 8
                      ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-300'
                      : 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-300'
                  }`}>
                    {shortHours <= 0
                      ? 'Pick a time'
                      : shortHours > 8
                      ? `${shortHours.toFixed(2)}h (max 8)`
                      : `${shortHours.toFixed(2)} hr${shortHours === 1 ? '' : 's'}`}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Start date</label>
                  <input type="date" className="input" {...register('startDate', { required: true })} />
                </div>
                <div>
                  <label className="label">End date</label>
                  <input type="date" className="input" {...register('endDate', { required: true })} />
                </div>
              </div>
            )}
          </Card>

          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2"><FileText size={16} /> Reason & supporting docs</div>
            <div>
              <label className="label">Reason</label>
              <textarea
                rows={5}
                className="input"
                placeholder="Briefly describe the reason for your leave..."
                {...register('reason', { required: 'Reason is required' })}
              />
              {errors.reason && <p className="text-xs text-rose-500 mt-1">{errors.reason.message}</p>}
            </div>
            <div className="mt-3">
              <label className="label">Attachment <span className="text-slate-400 font-normal">(optional)</span></label>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="btn-secondary cursor-pointer">
                  <Paperclip size={14} />
                  {file ? file.name : 'Attach a file'}
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} />
                </label>
                {file && (
                  <button type="button" className="btn-ghost text-rose-500" onClick={() => setFile(null)}>
                    <X size={14} /> Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">Useful for medical certificates etc.</p>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <div className="font-semibold mb-3">Summary</div>
            <ul className="text-sm space-y-2">
              <li className="flex justify-between gap-2">
                <span className="text-slate-500">Type</span>
                <span className="font-medium text-right">{typeMeta?.label || type}</span>
              </li>
              {isShort ? (
                <>
                  <li className="flex justify-between gap-2">
                    <span className="text-slate-500">Date</span>
                    <span className="font-medium text-right">{startDate ? dayjs(startDate).format('DD MMM YYYY') : '—'}</span>
                  </li>
                  <li className="flex justify-between gap-2">
                    <span className="text-slate-500">Window</span>
                    <span className="font-medium text-right">{startTime} – {endTime}</span>
                  </li>
                  <li className="flex justify-between gap-2">
                    <span className="text-slate-500">Duration</span>
                    <span className={`font-semibold text-right ${shortHours > 8 ? 'text-rose-600' : 'text-brand-600'}`}>
                      {shortHours > 0 ? `${shortHours.toFixed(2)} hr${shortHours === 1 ? '' : 's'}` : '—'}
                    </span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex justify-between gap-2">
                    <span className="text-slate-500">Start</span>
                    <span className="font-medium text-right">{startDate ? dayjs(startDate).format('DD MMM YYYY') : '—'}</span>
                  </li>
                  <li className="flex justify-between gap-2">
                    <span className="text-slate-500">End</span>
                    <span className="font-medium text-right">{endDate ? dayjs(endDate).format('DD MMM YYYY') : '—'}</span>
                  </li>
                  <li className="flex justify-between gap-2">
                    <span className="text-slate-500">Days</span>
                    <span className="font-semibold text-brand-600 text-right">
                      {dayCount > 0 ? `${dayCount} day${dayCount === 1 ? '' : 's'}` : '—'}
                    </span>
                  </li>
                </>
              )}
            </ul>

            <div className="mt-4 flex flex-col gap-2">
              <button
                type="submit"
                form="newLeaveForm"
                className="btn-primary w-full"
                disabled={isSubmitting || (isShort && (shortHours <= 0 || shortHours > 8))}
              >
                <Send size={14} /> {isSubmitting ? 'Submitting...' : 'Submit request'}
              </button>
              <button type="button" onClick={() => navigate('/leaves')} className="btn-secondary w-full">
                Cancel
              </button>
            </div>
          </Card>

          <Card>
            <div className="font-semibold mb-2">Tips</div>
            <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1.5">
              <li>• Use <b>Short leave</b> for quick 1–2 hour breaks like doctor visits.</li>
              <li>• Apply ahead of time so your manager can plan.</li>
              <li>• Attach medical certificates for sick leave when needed.</li>
              <li>• You can cancel a pending request from the leaves page.</li>
            </ul>
          </Card>
        </div>
      </form>
    </div>
  );
}
